import { Model, Ordenable } from '@/models/Model';
import { compact, flattenDeep, zip } from 'lodash';

export const alternate = <T>(...results: T[][]): T[] =>
  compact(flattenDeep(zip(...results)));

export const joinBy = <T extends Model, K extends keyof T>(
  results: T[][],
  key: K,
): T[] => {
  const map = new Map<T[K], T>();
  results.forEach(result => {
    result.forEach(item => {
      map.set(item[key], {
        ...map.get(item[key]),
        ...item,
      });
    });
  });
  return Array.from(map.values());
};

export const joinById = <T extends Model>(...results: T[][]): T[] => {
  return joinBy(results, 'id');
};

export const reorder = <T extends Model & Ordenable, K extends keyof T = 'id'>(
  items: T[],
  itemKeys: T[K][],
  key: K = 'id' as K,
): T[] => {
  const map = new Map(items.map(item => [item[key], item]));
  return compact(itemKeys.map(k => map.get(k))).map((item, index) => ({
    ...item,
    order: index + 1,
  }));
};

export const reorderMap = <T extends Model & Ordenable>(
  map: Record<string, T[]>,
): Record<string, T[]> => {
  return Object.fromEntries(
    Object.entries(map).map(([key, children]) => [
      key,
      reorder(
        children,
        children.map(c => c.id),
      ),
    ]),
  );
};

export const sortByOrder = <T extends Ordenable>(items: T[]): T[] => {
  return items.sort((a, b) => a.order - b.order);
};

export const getNextOrder = <T extends Ordenable>(items: T[]): number =>
  items.reduce((max, current) => Math.max(max, current.order), 0) + 1;
